import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Layers } from 'lucide-react';
import { Button } from '../../../../components/ui/Button';
import { ProgressBar } from '../../../../components/ui/ProgressBar';
import { ToneBadge } from '../../../../components/ui/ToneBadge';
import { OperatorActionBar } from '../../components/OperatorActionBar';
import { OrderQueueScan } from '../../components/OrderQueueScan';
import { QueueList } from '../../components/QueueList';
import { SelectableTaskCard } from '../../components/SelectableTaskCard';
import { TaskMeta } from '../../components/TaskMeta';
import { taskEntered } from '../../../../features/operator/operatorDeviceSlice';
import { OPERATOR_FLOW } from '../../../../features/operator/operatorConstants';
import {
  orderCardTitle,
  selectOrderReserveReady,
  selectPickingRouteReady,
} from '../../../../features/operator/operatorUtils';
import { fetchPickingQueue } from '../../../../features/operatorPicking/operatorPickingThunks';
import {
  pickingSelectionCleared,
  pickingSelectionToggled,
} from '../../../../features/operatorPicking/operatorPickingRouteSlice';
import { MAX_ROUTE_ORDERS, routePath } from '../../../../features/operatorPicking/operatorPickingRouteUtils';
import { linesText, needsReserve, reserveTagText } from '../../../../features/operatorPicking/operatorPickingUtils';
import { formatQuantity, wmsOrderStateLabel } from '../../../../utils/inventoryLabels';

/** «Pickear 3 juntos»; one alone still goes by route. */
const batchLabel = (count) => (count === 1 ? 'Pickear 1 por ruta' : `Pickear ${count} juntos`);

/**
 * Picking: the orders waiting to be picked, in the tray's order. Scanning an
 * order opens it, and so does tapping its card. Its box chooses it for a
 * batch — up to ten —, and «Pickear N juntos» walks them in one route; the
 * boxes show once the backend serves routes. An order its client's contract
 * does not reserve on entry says it is not reserved yet: its walk can.
 */
export const PickingQueue = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { queue } = useSelector((state) => state.operatorPicking);
  const routeReady = useSelector(selectPickingRouteReady);
  const reserveReady = useSelector(selectOrderReserveReady);
  const chosen = useSelector((state) => state.operatorPickingRoute.selection);
  const selection = routeReady ? chosen : [];
  const full = selection.length >= MAX_ROUTE_ORDERS;

  useEffect(() => {
    dispatch(taskEntered({ flow: OPERATOR_FLOW.PICKING }));
    dispatch(fetchPickingQueue({ page: 1 }));
  }, [dispatch]);

  const startBatch = () => {
    const path = routePath(selection);
    dispatch(pickingSelectionCleared());
    navigate(path);
  };

  return (
    <div className="space-y-5">
      <OrderQueueScan flow={OPERATOR_FLOW.PICKING} />

      {selection.length > 0 && (
        <p data-testid="batch-count" className="text-sm font-semibold text-ninja-dark dark:text-white">
          {full
            ? `${MAX_ROUTE_ORDERS} elegidos: es el máximo por ruta.`
            : `${selection.length} de ${MAX_ROUTE_ORDERS} elegidos para pickear juntos.`}
        </p>
      )}

      <QueueList
        queue={queue}
        testId="picking-order-list"
        emptyText="No hay órdenes por pickear."
        onRetry={() => dispatch(fetchPickingQueue({ page: 1 }))}
        onMore={() => dispatch(fetchPickingQueue({ page: queue.page + 1 }))}
        onRefresh={() => dispatch(fetchPickingQueue({ page: 1 }))}
        refreshThunk={fetchPickingQueue}
      >
        {queue.items.map((order) => (
          <li key={order.id}>
            <SelectableTaskCard
              selectable={routeReady}
              selected={selection.includes(order.id)}
              // An order waiting for stock has nothing a route could take.
              selectDisabled={full || Boolean(order.waiting_stock)}
              onToggle={() => dispatch(pickingSelectionToggled(order.id))}
              selectData={{ 'data-order-id': order.id }}
              to={`/picking/${order.id}`}
              testId="picking-order-card"
              data={{ 'data-order-id': order.id, 'data-waiting-stock': String(Boolean(order.waiting_stock)) }}
              title={orderCardTitle(order)}
              subtitle={[order.recipient, order.company_name, linesText(order.lines_count)].filter(Boolean).join(' · ')}
              aside={
                <span className="rounded-full border border-border px-2.5 py-0.5 text-xs font-semibold text-text-secondary">
                  {wmsOrderStateLabel(order.wms_state)}
                </span>
              }
            >
              <div className="space-y-3">
                <ProgressBar
                  size="sm"
                  value={Number(order.prepared_units) || 0}
                  max={Number(order.units) || 0}
                  label={`${formatQuantity(order.prepared_units)} de ${formatQuantity(order.units)} un pickeadas`}
                />
                <TaskMeta task={order}>
                  {reserveReady && needsReserve(order) && (
                    <ToneBadge tone="warning" testId="picking-reserve-chip">
                      {reserveTagText(order)}
                    </ToneBadge>
                  )}
                </TaskMeta>
              </div>
            </SelectableTaskCard>
          </li>
        ))}
      </QueueList>

      {selection.length > 0 && (
        <div data-testid="batch-bar" data-count={selection.length}>
          <OperatorActionBar>
            <Button variant="ghost" size="lg" onClick={() => dispatch(pickingSelectionCleared())} data-testid="batch-clear">
              Quitar ({selection.length})
            </Button>
            <Button variant="primary" size="lg" icon={Layers} onClick={startBatch} data-testid="batch-start">
              {batchLabel(selection.length)}
            </Button>
          </OperatorActionBar>
        </div>
      )}
    </div>
  );
};
